import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import LiveTeeTimesScroller from '../components/LiveTeeTimesScroller';
import { Clock, Bell, MapPin, ArrowRight, Settings, Search, Mail } from 'lucide-react';

function WelcomePage() {
  const faqData = [
    {
      question: "What is Snag Your Tee and how does it help Vancouver golfers?",
      answer: "Snag Your Tee is a tool that helps golfers in Vancouver, BC find available tee times and join waitlists when courses are full. Instead of checking multiple booking sites, we track tee sheet availability for you—so you can focus on your game."
    },
    {
      question: "Which golf courses in Vancouver are included?",
      answer: "We monitor 9 golf courses across Metro Vancouver: Langara, Fraserview, and McCleery in Vancouver; Burnaby Mountain and Riverway in Burnaby; Country Meadows, Savage Creek, and Mylora in Richmond; plus UBC Golf Course. All courses are updated in real-time for availability."
    },
    {
      question: "How does the tee time waitlist work?",
      answer: "When tee times are sold out, you can join our waitlist. We continuously check for cancellations or last-minute openings, and notify you right away when a spot becomes available."
    },
    {
      question: "Do I need to pay to use Snag Your Tee?",
      answer: "No. Snag Your Tee is free to use while we're building the platform. In the future, we may introduce premium features like SMS alerts, but the core service will remain free."
    },
    {
      question: "How long does it take to find suitable tee times?",
      answer: "This depends on your criteria and course availability. Typically, suitable time slots appear within 1-3 days."
    },
    {
      question: "How do I cancel alerts?",
      answer: "After receiving an alert email, simply click the unsubscribe link in the email to stop the alert service."
    },
    {
      question: "Will I receive spam emails?",
      answer: "No. We only send alerts when we find matching tee time slots. We never send promotional emails."
    },
    {
      question: "Can I book tee times directly through Snag Your Tee?",
      answer: "Not yet. Once you find an available tee time, we redirect you to the official course booking page to confirm your reservation."
    },
    {
      question: "How does Snag Your Tee save time compared to checking individual Vancouver golf course websites?",
      answer: "Tee times in Vancouver can disappear in minutes. We save you time by monitoring multiple courses at once and sending you alerts in real time."
    },
    {
      question: "Do you only cover Vancouver?",
      answer: "Our focus is Vancouver right now, but golfers in Burnaby, Richmond, and Metro Vancouver can also benefit. We're expanding to more courses across the region."
    },
    {
      question: "What if I want last-minute tee times?",
      answer: "That's exactly where Snag Your Tee helps—last-minute tee times are hard to catch. Our real-time monitoring increases your chance of snagging a spot even when others think the course is full."
    }
  ];

  return (
    <Layout>
      
      {/* Combined Hero + Golf Section - Unified Background */}
      <div className="bg-gradient-to-b from-green-50 to-white">
        {/* Hero Section - Reduced Height */}
        <div className="h-[65vh] flex items-center justify-center px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Find Golf Tee Times in Vancouver, BC
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Helping Vancouver golfers book tee times, track availability, and join waitlists with ease.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/waitlist"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Join Waitlist Now
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/tee-times"
                className="inline-flex items-center px-6 py-3 border border-green-600 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-all duration-300 transform hover:scale-105"
              >
                Browse Tee Times
              </Link>
            </div>
          </div>
        </div>

        {/* Golf Scrolling Section */}
        <div className="py-8 mb-20">
          <LiveTeeTimesScroller />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Book Tee Times Easily Section - Left Text + Right Cards */}
        <div className="mb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Text Content */}
            <div className="lg:pr-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Let's golf in Vancouver, BC
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Join our waitlist to get notified when your preferred Vancouver golf times become available. Check multiple golf courses in one place.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
                    <MapPin className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Multiple Golf Courses</h3>
                    <p className="text-gray-600">View available tee times from 9 Vancouver, Burnaby, and Richmond golf courses in one place</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
                    <Clock className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Smart Waitlist</h3>
                    <p className="text-gray-600">Join the waitlist for your preferred times and dates - get alerted when spots open up</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
                    <Bell className="text-green-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Email Notifications</h3>
                    <p className="text-gray-600">Get notified by email when your preferred Vancouver golf times become available</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Interactive Feature Cards */}
            <div className="relative">
              <div className="space-y-6">
                <div className="group bg-white rounded-xl p-6 shadow-lg border border-green-100 transform rotate-1 hover:rotate-0 transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full group-hover:scale-125 transition-transform duration-300"></div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-green-700 transition-colors duration-300">Course Overview</span>
                  </div>
                  <p className="text-gray-600 text-sm group-hover:text-gray-800 transition-colors duration-300">9 courses • Live availability</p>
                  <div className="mt-3 bg-green-50 group-hover:bg-green-100 rounded-lg p-3 transition-colors duration-300">
                    <p className="text-xs text-green-700 group-hover:text-green-800 transition-colors duration-300">✓ Langara, Fraserview, McCleery...</p>
                  </div>
                </div>
                
                <div className="group bg-white rounded-xl p-6 shadow-lg border border-blue-100 transform -rotate-1 ml-8 hover:rotate-0 hover:ml-6 transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-3 h-3 bg-blue-500 rounded-full group-hover:scale-125 transition-transform duration-300 group-hover:animate-pulse"></div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700 transition-colors duration-300">Waitlist Alert</span>
                  </div>
                  <p className="text-gray-600 text-sm group-hover:text-gray-800 transition-colors duration-300">Fraserview 2:30 PM - 2 players</p>
                  <div className="mt-3 bg-blue-50 group-hover:bg-blue-100 rounded-lg p-3 transition-colors duration-300">
                    <p className="text-xs text-blue-700 group-hover:text-blue-800 transition-colors duration-300">📧 Your waitlist match found!</p>
                  </div>
                </div>
                
                <div className="group bg-white rounded-xl p-6 shadow-lg border border-orange-100 transform rotate-1 hover:rotate-0 transition-all duration-300 cursor-pointer hover:shadow-xl hover:scale-105">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-3 h-3 bg-orange-500 rounded-full group-hover:scale-125 transition-transform duration-300"></div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-orange-700 transition-colors duration-300">Quick Action</span>
                  </div>
                  <p className="text-gray-600 text-sm group-hover:text-gray-800 transition-colors duration-300">Book now before it's gone!</p>
                  <div className="mt-3 bg-orange-50 group-hover:bg-orange-100 rounded-lg p-3 transition-colors duration-300">
                    <p className="text-xs text-orange-700 group-hover:text-orange-800 transition-colors duration-300 group-hover:animate-bounce">⏰ Act fast - limited time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </main>

      {/* Join the Waitlist Section - Full Width Background */}
      <div className="bg-gradient-to-b from-blue-50 to-indigo-50 py-20 mb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Interactive Step Visualization */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                {/* Connection Line */}
                <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-gradient-to-b from-green-300 via-blue-300 to-orange-300 opacity-60"></div>
                
                <div className="space-y-12">
                  <div className="group flex items-start space-x-6 cursor-pointer">
                    <div className="relative z-10 p-4 bg-green-100 rounded-full group-hover:bg-green-200 group-hover:scale-110 transition-all duration-300 group-hover:shadow-lg">
                      <Settings className="text-green-600 group-hover:text-green-700 transition-colors duration-300" size={32} />
                    </div>
                    <div className="pt-2 group-hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-green-600 group-hover:bg-green-700 text-white rounded-full w-8 h-8 flex items-center justify-center mb-3 text-sm font-bold transition-colors duration-300 group-hover:shadow-md">
                        1
                      </div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-green-700 transition-colors duration-300">Set Preferences</h4>
                      <p className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                        Choose golf courses in Vancouver, BC, dates, and time preferences
                      </p>
                    </div>
                  </div>
                  
                  <div className="group flex items-start space-x-6 cursor-pointer">
                    <div className="relative z-10 p-4 bg-blue-100 rounded-full group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300 group-hover:shadow-lg">
                      <Search className="text-blue-600 group-hover:text-blue-700 transition-colors duration-300" size={32} />
                    </div>
                    <div className="pt-2 group-hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-blue-600 group-hover:bg-blue-700 text-white rounded-full w-8 h-8 flex items-center justify-center mb-3 text-sm font-bold transition-colors duration-300 group-hover:shadow-md">
                        2
                      </div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-blue-700 transition-colors duration-300">Browse Available Times</h4>
                      <p className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                        Check live tee times across multiple Vancouver golf courses, all in one convenient place
                      </p>
                    </div>
                  </div>
                  
                  <div className="group flex items-start space-x-6 cursor-pointer">
                    <div className="relative z-10 p-4 bg-orange-100 rounded-full group-hover:bg-orange-200 group-hover:scale-110 transition-all duration-300 group-hover:shadow-lg">
                      <Mail className="text-orange-600 group-hover:text-orange-700 transition-colors duration-300" size={32} />
                    </div>
                    <div className="pt-2 group-hover:translate-x-2 transition-transform duration-300">
                      <div className="bg-orange-600 group-hover:bg-orange-700 text-white rounded-full w-8 h-8 flex items-center justify-center mb-3 text-sm font-bold transition-colors duration-300 group-hover:shadow-md">
                        3
                      </div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-orange-700 transition-colors duration-300">Get Notified</h4>
                      <p className="text-gray-600 group-hover:text-gray-800 transition-colors duration-300">
                        Instant alerts when Vancouver tee times become available
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Text Content */}
            <div className="order-1 lg:order-2 lg:pl-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Join the Waitlist
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Getting started is simple. Set up your preferences once and let our system do all the work for you. Join thousands of Vancouver golfers who never miss their perfect tee time.
              </p>
              
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20">
                <h3 className="font-semibold text-gray-800 mb-2">Why Join Now?</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Free service, no hidden fees</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Setup takes less than 2 minutes</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span>Cancel anytime with one click</span>
                  </li>
                </ul>
              </div>
              
              <Link
                to="/waitlist"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Join Waitlist Now
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 md:px-6">

        {/* FAQ */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {faqData.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6 h-fit">
                <h4 className="text-base font-semibold text-gray-800 mb-3">
                  {faq.question}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default WelcomePage;